import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import { useCampusOptions } from "../groups/group-queries";
import {
  REGISTRATION_LABEL,
  VISIBILITY_LABEL,
  useEventCategories,
  useSaveEvent,
  useSetEventActive,
  type EventFields,
  type EventRow,
  type EventVisibility,
  type RegistrationRule,
} from "./event-queries";
import { moneyValue } from "../../lib/money-input";

const VISIBILITIES = (Object.keys(VISIBILITY_LABEL) as EventVisibility[]).map((value) => ({
  value,
  label: VISIBILITY_LABEL[value],
}));
const RULES = (Object.keys(REGISTRATION_LABEL) as RegistrationRule[]).map((value) => ({
  value,
  label: REGISTRATION_LABEL[value],
}));

const EMPTY: EventFields = {
  title: "",
  slug: "",
  description: "",
  categoryId: null,
  campusId: null,
  ministry: "",
  location: "",
  visibility: "public",
  registration: "none",
  capacity: null,
  priceCents: null,
};

export interface EventModalProps {
  /** null to add an event, a row to edit it; undefined when closed. */
  readonly event: EventRow | null | undefined;
  readonly canManage: boolean;
  readonly onClose: () => void;
  readonly onCreated?: (eventId: string) => void;
}

/** One dialog for adding and editing an event; dates are added on the record, not here. */
export function EventModal({ event, canManage, onClose, onCreated }: EventModalProps) {
  const save = useSaveEvent();
  const setActive = useSetEventActive();
  const categories = useEventCategories();
  const campuses = useCampusOptions();
  const [fields, setFields] = useState<EventFields>(
    event === null || event === undefined
      ? EMPTY
      : {
          title: event.title,
          slug: event.slug,
          description: event.description ?? "",
          categoryId: event.categoryId,
          campusId: event.campusId,
          ministry: event.ministry ?? "",
          location: event.location ?? "",
          visibility: event.visibility,
          registration: event.registration,
          capacity: event.capacity,
          priceCents: event.priceCents,
        },
  );
  const [confirming, setConfirming] = useState(false);
  const text =
    (key: "title" | "slug" | "description" | "ministry" | "location") =>
    (e: { currentTarget: { value: string } }) => {
      const value = e.currentTarget.value;
      setFields((current) => ({ ...current, [key]: value }));
    };
  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another event already uses that title or address."
      : code === "P0002"
        ? "The category or campus is not one of this church's. Choose again."
        : save.isError
          ? "The event was not saved. Check your access and try again."
          : null;

  return (
    <>
      <Modal
        opened={event !== undefined}
        onClose={onClose}
        title={event === null ? "Add an event" : "Edit event"}
        radius="lg"
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            disabled={!canManage}
            value={fields.title}
            onChange={text("title")}
          />
          <Textarea
            label="Description"
            description="What a member reads before registering, and what the website shows for a public event."
            disabled={!canManage}
            value={fields.description}
            onChange={text("description")}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group grow align="flex-start">
            <Select
              label="Category"
              placeholder="None"
              clearable
              disabled={!canManage}
              data={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={fields.categoryId}
              onChange={(value) => {
                setFields((current) => ({ ...current, categoryId: value }));
              }}
            />
            <TextInput
              label="Ministry"
              placeholder="Kids, Worship, Welcome"
              disabled={!canManage}
              value={fields.ministry}
              onChange={text("ministry")}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Campus"
              placeholder="None"
              clearable
              disabled={!canManage}
              data={campuses.data ?? []}
              value={fields.campusId}
              onChange={(value) => {
                setFields((current) => ({ ...current, campusId: value }));
              }}
            />
            <TextInput
              label="Where"
              placeholder="Main hall, or an address"
              disabled={!canManage}
              value={fields.location}
              onChange={text("location")}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Who can see it"
              description="Public is on the website and in the portal; unlisted needs the link; private is those registered and staff."
              disabled={!canManage}
              data={VISIBILITIES}
              value={fields.visibility}
              allowDeselect={false}
              onChange={(value) => {
                const v = VISIBILITIES.find((o) => o.value === value)?.value;
                if (v !== undefined) setFields((current) => ({ ...current, visibility: v }));
              }}
            />
            <Select
              label="Registration"
              description="Just come needs nothing; Register takes registrations; closed keeps the event listed."
              disabled={!canManage}
              data={RULES}
              value={fields.registration}
              allowDeselect={false}
              onChange={(value) => {
                const v = RULES.find((o) => o.value === value)?.value;
                if (v !== undefined) setFields((current) => ({ ...current, registration: v }));
              }}
            />
            <NumberInput
              label="Capacity"
              description="Per date. A member beyond it is waitlisted; staff may always add."
              placeholder="No limit"
              disabled={!canManage}
              min={1}
              max={100000}
              value={fields.capacity ?? ""}
              onChange={(value) => {
                setFields((current) => ({
                  ...current,
                  capacity: typeof value === "number" ? value : null,
                }));
              }}
            />
            <NumberInput
              label="Price"
              description="Per person, in dollars. Members pay in the portal; the website's guest form stays free."
              placeholder="Free"
              disabled={!canManage}
              min={0}
              max={100000}
              decimalScale={2}
              fixedDecimalScale
              prefix="$"
              value={fields.priceCents === null ? "" : fields.priceCents / 100}
              onChange={(value) => {
                const dollars = moneyValue(value);
                setFields((current) => ({
                  ...current,
                  priceCents: dollars !== "" && dollars > 0 ? Math.round(dollars * 100) : null,
                }));
              }}
            />
          </Group>
          {event !== null && event !== undefined ? (
            <TextInput
              label="Address"
              description="The event's address on the website and in links. Changing it breaks links people have."
              disabled={!canManage}
              value={fields.slug}
              onChange={text("slug")}
            />
          ) : null}
          {errorText !== null ? (
            <Text size="sm" c="red">
              {errorText}
            </Text>
          ) : null}
          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {event !== null && event !== undefined && canManage ? (
                event.isActive ? (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setConfirming(true);
                    }}
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    loading={setActive.isPending}
                    onClick={() => {
                      setActive.mutate(
                        { eventId: event.id, isActive: true },
                        { onSuccess: onClose },
                      );
                    }}
                  >
                    Restore
                  </Button>
                )
              ) : null}
            </Group>
            {canManage ? (
              <Group gap="sm">
                <Button variant="subtle" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  disabled={fields.title.trim().length === 0}
                  loading={save.isPending}
                  onClick={() => {
                    save.mutate(
                      { eventId: event === null || event === undefined ? null : event.id, fields },
                      {
                        onSuccess: (id) => {
                          if (event === null && onCreated !== undefined) onCreated(id);
                          else onClose();
                        },
                      },
                    );
                  }}
                >
                  {event === null ? "Add event" : "Save event"}
                </Button>
              </Group>
            ) : (
              <Button variant="subtle" onClick={onClose}>
                Close
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Archive this event"
        message={
          event === null || event === undefined
            ? ""
            : `Archive ${event.title}? Its dates and registrations stay; it stops appearing to members and on the website. You can restore it later.`
        }
        confirmLabel="Archive"
        danger
        loading={setActive.isPending}
        onConfirm={() => {
          if (event === null || event === undefined) return;
          setActive.mutate(
            { eventId: event.id, isActive: false },
            {
              onSuccess: () => {
                setConfirming(false);
                onClose();
              },
            },
          );
        }}
      />
    </>
  );
}
